/**
 * useInputMode Hook
 * 
 * Handles input mode switching between voice (VAD), keyboard, and PTT:
 * - Voice/keyboard/PTT mode switching
 * - PTT press/release handlers
 * - Avatar audio toggle
 * - Send text message functionality
 * - Microphone fallback detection
 * 
 * XSTATE MIGRATION: Now dispatches events to state machine instead of direct setState.
 */

import { useCallback, useRef } from 'react';
import { WebSocketService } from '../../services/realtime/WebSocketService';
import { WebAudioManager } from '../../services/audio';
import type { SessionTrackingCallbacks } from '../../types/callbacks';
import { logger } from '../../utils/logger';
import { generateKeyboardMessageId, WAV_SAMPLE_RATE, WAV_NUM_CHANNELS } from './constants';
import { createWavBlob, combineAudioChunks } from './utils';
import type { TranscriptMessage, SendFunction } from './types';

// ============================================================================
// Types
// ============================================================================

export interface UseInputModeParams {
  send: SendFunction;
  websocketServiceRef: React.MutableRefObject<WebSocketService | null>;
  audioManagerRef: React.MutableRefObject<WebAudioManager | null>;
  boundSessionTrackingIdRef: React.MutableRefObject<string | undefined>;
  sessionTracking?: SessionTrackingCallbacks;
  /** Ref for recording audio chunks (for WAV blob debug playback) */
  isRecordingForBlobRef: React.MutableRefObject<boolean>;
  recordedAudioChunksRef: React.MutableRefObject<Uint8Array[]>;
  /**
   * Reset speech-lifecycle refs/timers (safety timeout, recording state).
   * Called on every mode switch to prevent phantom speech detection from
   * the previous mode leaking into the new one.
   */
  resetSpeechState?: () => void;
}

export interface UseInputModeReturn {
  setInputMode: (mode: 'voice' | 'keyboard' | 'ptt', options?: { enableMicrophone?: boolean }) => Promise<void>;
  toggleAvatarAudio: () => void;
  sendTextMessage: (text: string) => void;
  handlePTTPressed: () => void;
  handlePTTReleased: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useInputMode({
  send,
  websocketServiceRef,
  audioManagerRef,
  boundSessionTrackingIdRef,
  sessionTracking,
  isRecordingForBlobRef,
  recordedAudioChunksRef,
  resetSpeechState,
}: UseInputModeParams): UseInputModeReturn {
  // Track current PTT placeholder ID for audioBlob association
  const currentPTTIdRef = useRef<string | null>(null);

  // Switch between voice, keyboard, and PTT input modes
  const setInputMode = useCallback(async (mode: 'voice' | 'keyboard' | 'ptt', options?: { enableMicrophone?: boolean }): Promise<void> => {
    const audioManager = audioManagerRef.current;
    const websocketService = websocketServiceRef.current;
    const enableMicrophone = options?.enableMicrophone !== false;

    // 🐛 FIX: Reset any stuck user-speech state before switching modes.
    // If the previous mode left the conversation state machine trapped in
    // userSpeaking/waitingForTranscript (e.g. server VAD fired on degraded
    // audio in keyboard mode but never sent speech_stopped), subsequent
    // PTT_PRESSED events would be silently dropped — causing the PTT deadlock
    // (Symptom 4) and the "listening forever" indicator (Symptom 2).
    // Dispatching RESET_USER_SPEECH clears phantom placeholders and visual state.
    send({ type: 'RESET_USER_SPEECH' });

    // 🐛 FIX: Clear speech-lifecycle refs/timers (safety timeout, recording
    // state) so they don't fire after the mode switch and re-trigger phantom
    // speech state in the new mode.
    resetSpeechState?.();

    // PTT mode: needs mic + turn_detection: null
    if (mode === 'ptt' && enableMicrophone) {
      try {
        await audioManager?.ensureMicrophoneReady();

        // Dispatch event to state machine only after mic is ready
        send({ type: 'SET_INPUT_MODE', mode, enableMicrophone });

        // Enable PTT mode on audio manager (disables VAD, controls streaming)
        audioManager?.setPTTMode?.(true);

        if (!audioManager?.isRecordingActive()) {
          audioManager?.startRecording();
        } else {
          audioManager?.resumeMicrophone();
        }

        // Disable server-side turn detection for PTT
        // (user controls start/stop via button, we commit manually)
        // 🚨 GA API: turn_detection lives inside audio.input (not at session top level)
        if (websocketService?.isConnected()) {
          await websocketService.updateSessionConfig({
            type: 'realtime',
            audio: {
              input: {
                turn_detection: null,
              },
            },
          });
        }

        logger.info('[🎙️ PTT] Switched to PTT mode - turn_detection disabled');
      } catch (error) {
        console.warn('[🎙️ PTT] Failed to enable microphone, falling back to keyboard mode:', error);
        send({ type: 'SET_INPUT_MODE', mode: 'keyboard' });
        send({ type: 'SET_MICROPHONE_FALLBACK', enabled: true });
        send({ type: 'CONNECTION_ERROR', error: error as Error });
      }
      return;
    }

    // Voice (VAD) mode: needs mic + turn_detection: semantic_vad
    if (mode === 'voice' && enableMicrophone) {
      try {
        await audioManager?.ensureMicrophoneReady();

        // Dispatch event to state machine only after mic is ready
        send({ type: 'SET_INPUT_MODE', mode, enableMicrophone });

        // Disable PTT mode, re-enable VAD
        audioManager?.setPTTMode?.(false);

        if (!audioManager?.isRecordingActive()) {
          audioManager?.startRecording();
        } else {
          audioManager?.resumeMicrophone();
        }

        // Re-enable server-side turn detection for VAD mode
        // 🚨 GA API: turn_detection lives inside audio.input (not at session top level)
        if (websocketService?.isConnected()) {
          await websocketService.updateSessionConfig({
            type: 'realtime',
            audio: {
              input: {
                turn_detection: {
                  type: 'semantic_vad',
                  eagerness: 'high',
                },
              },
            },
          });
        }

        logger.info('[🎤 VAD] Switched to voice (VAD) mode - turn_detection enabled');
      } catch (error) {
        console.warn('[⌨️/🎤] Failed to enable microphone, staying in keyboard mode:', error);
        send({ type: 'SET_INPUT_MODE', mode: 'keyboard' });
        send({ type: 'SET_MICROPHONE_FALLBACK', enabled: true });
        send({ type: 'CONNECTION_ERROR', error: error as Error });
      }
      return;
    }

    // Keyboard mode or voice/ptt without microphone
    send({ type: 'SET_INPUT_MODE', mode, enableMicrophone });

    if (mode === 'keyboard') {
      // Pause microphone and disable PTT when switching to keyboard mode
      audioManager?.setPTTMode?.(false);
      audioManager?.pauseMicrophone();

      // 🐛 FIX: Disable server-side turn detection when entering keyboard mode.
      // Without this, the OpenAI Realtime server keeps semantic_vad active and
      // continues detecting "speech" from silent/degraded audio frames that leak
      // through disabled mic tracks (especially on Safari/iOS). This causes:
      //  - Phantom transcriptions of garbage/phonetic text (Symptom 1)
      //  - "Listening" indicator stuck on forever (Symptom 2)
      //  - User questions not accepted because server is mid-speech-detection (Symptom 3)
      //  - Conversation state machine stuck in userSpeaking → PTT deadlock (Symptom 4)
      //  - Voice mode VAD left in confused state on subsequent switch (Symptom 5)
      if (websocketService?.isConnected()) {
        await websocketService.updateSessionConfig({
          type: 'realtime',
          audio: {
            input: {
              turn_detection: null,
            },
          },
        });
        // Clear any audio the server may have buffered from the phantom detection
        websocketService.clearAudioBuffer();
      }

      logger.info('[⌨️ Keyboard] Switched to keyboard mode - turn_detection disabled, mic paused');
    }
  }, [send, audioManagerRef, websocketServiceRef, resetSpeechState]);

  // PTT: User pressed the push-to-talk button
  // NOTE: Avatar interruption does NOT happen here. It happens when the AudioRecording
  // layer detects actual speech (onPTTSpeechDetected event), which triggers cancelResponse
  // via useWebSocketSession. This way, simply touching the button doesn't interrupt the AI.
  const handlePTTPressed = useCallback(() => {
    const audioManager = audioManagerRef.current;
    
    if (!audioManager?.isPTTModeActive?.()) {
      logger.warn('[🎙️ PTT] handlePTTPressed called but not in PTT mode');
      return;
    }

    // Generate PTT placeholder ID and store for blob association later
    const pttId = `ptt-${Date.now()}`;
    currentPTTIdRef.current = pttId;

    // Start recording audio chunks for WAV blob (debug playback)
    recordedAudioChunksRef.current = [];
    isRecordingForBlobRef.current = true;

    // Start streaming audio to WebSocket
    audioManager.startPTTTransmit();
    
    // Dispatch event to state machine (creates placeholder, shows waveform)
    send({ type: 'PTT_PRESSED', pttId });
    
    logger.debug('[🎙️ PTT] Button pressed - transmitting (avatar keeps playing until speech detected)');
  }, [send, audioManagerRef, isRecordingForBlobRef, recordedAudioChunksRef]);

  // PTT: User released the push-to-talk button
  const handlePTTReleased = useCallback(() => {
    const audioManager = audioManagerRef.current;
    const websocketService = websocketServiceRef.current;
    
    if (!audioManager?.isPTTModeActive?.()) {
      logger.warn('[🎙️ PTT] handlePTTReleased called but not in PTT mode');
      return;
    }

    // Stop streaming audio and check if speech was detected during this PTT session.
    // The VAD runs alongside PTT to filter out "user pressed button but didn't speak".
    const speechDetected = audioManager.stopPTTTransmit();

    // Stop recording for WAV blob
    isRecordingForBlobRef.current = false;

    // ========================================================================
    // SILENCE: No speech detected → just ambient noise / silence.
    // Clear the buffer and go back to waiting without triggering a response.
    // ========================================================================
    if (!speechDetected) {
      logger.info('[🎙️ PTT] No speech detected - discarding');
      
      recordedAudioChunksRef.current = [];
      
      // Clear OpenAI's audio buffer (discard the ambient noise we streamed)
      if (websocketService?.isConnected()) {
        websocketService.clearAudioBuffer();
      }
      
      // Remove placeholder and return to waiting state
      send({ type: 'PTT_SILENCE_DETECTED' });
      currentPTTIdRef.current = null;
      return;
    }

    // ========================================================================
    // SPEECH detected → normal flow, commit and get response.
    // ========================================================================
    let audioBlob: Blob | undefined;
    if (recordedAudioChunksRef.current.length > 0) {
      try {
        const combined = combineAudioChunks(recordedAudioChunksRef.current);
        audioBlob = createWavBlob(combined, WAV_SAMPLE_RATE, WAV_NUM_CHANNELS);
        logger.debug('[🎙️ PTT] Created audio blob:', audioBlob.size, 'bytes');
      } catch (error) {
        console.error('[🎙️ PTT] Failed to create audio blob:', error);
      }
      recordedAudioChunksRef.current = [];
    }
    
    // Dispatch event to state machine (transitions to waitingForTranscript)
    send({ type: 'PTT_RELEASED' });

    // Attach audio blob to the PTT placeholder message
    if (audioBlob && currentPTTIdRef.current) {
      send({ type: 'UPDATE_MESSAGE', id: currentPTTIdRef.current, updates: { audioBlob } });
    }
    currentPTTIdRef.current = null;
    
    // Commit audio buffer and request AI response
    if (websocketService?.isConnected()) {
      websocketService.commitAndCreateResponse();
    }
    
    logger.debug('[🎙️ PTT] Button released - committed audio, requesting response');
  }, [send, audioManagerRef, websocketServiceRef, isRecordingForBlobRef, recordedAudioChunksRef]);

  // Toggle avatar audio on/off
  const toggleAvatarAudio = useCallback(() => {
    // Dispatch event to state machine
    send({ type: 'TOGGLE_AVATAR_AUDIO' });
    
    // Note: The actual playback control is handled by the component consuming this hook
    // which should react to avatarAudioEnabled state changes
  }, [send]);

  // Send text message (keyboard mode)
  const sendTextMessage = useCallback((text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText) {
      console.warn('[⌨️ Keyboard Mode] Attempted to send empty message');
      return;
    }
    
    const websocketService = websocketServiceRef.current;
    if (!websocketService || !websocketService.isConnected()) {
      console.error('[⌨️ Keyboard Mode] WebSocket service not connected');
      return;
    }
    
    logger.info('[⌨️ Keyboard Mode] Sending text message. Length:', trimmedText.length);
    
    const messageId = generateKeyboardMessageId();
    const timestamp = Date.now();
    
    // Create user message
    const newMessage: TranscriptMessage = {
      text: trimmedText,
      timestamp: timestamp,
      speaker: 'user',
      id: messageId
    };
    
    // Add message via state machine
    send({ type: 'ADD_MESSAGE', message: newMessage });
    
    // Log to session tracking (if callbacks provided)
    if (boundSessionTrackingIdRef.current && sessionTracking?.logMessage) {
      sessionTracking.logMessage({
        text: trimmedText,
        timestamp: timestamp,
        speaker: 'user',
        id: messageId
      });
    }
    
    // Send message via WebSocket
    websocketService.sendText(trimmedText);
    
    logger.info('[⌨️ Keyboard Mode] Text sent - AI will respond automatically');
  }, [send, websocketServiceRef, boundSessionTrackingIdRef, sessionTracking]);

  return {
    setInputMode,
    toggleAvatarAudio,
    sendTextMessage,
    handlePTTPressed,
    handlePTTReleased,
  };
}
